import React from "react";
import PropTypes from "prop-types";
import styles from "./TagsTreeComponent.module.css";

/** Props:
 *  - tree: [{ name, fullPath, slug, children: [...] }]
 *  - onSelect(node)
 *  - selectedFullPath: string | null
 */
export default function TagsTreeComponent({ tree, onSelect, selectedFullPath }) {
  if (!Array.isArray(tree) || tree.length === 0) {
    return <div className="text-muted small">No tags</div>;
  }
  return (
    <ul className={styles.treeRoot} role="tree" aria-label="Tags tree">
      {tree.map((n) => (
        <TreeNode
          key={n.fullPath}
          node={n}
          onSelect={onSelect}
          selectedFullPath={selectedFullPath}
          depth={0}
        />
      ))}
    </ul>
  );
}

function TreeNode({ node, onSelect, selectedFullPath, depth }) {
  const isSelected = node.fullPath === selectedFullPath;
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;

  // Add a subtle textual indent using '---' per depth level
  const dashPrefix = depth > 0 ? `${Array(depth).fill("").join(" ")} ` : "";

  return (
    <li className={styles.nodeItem} data-depth={depth} role="treeitem" aria-expanded={hasChildren ? true : undefined}>
      <button
        type="button"
        className={`${styles.nodeBtn} ${isSelected ? styles.selected : ""}`}
        onClick={() => onSelect?.(node)}
        title={node.fullPath}
      >
        {/* purely decorative, hidden from screen readers */}
        {depth > 0 && (
          <span className={styles.dashPrefix} aria-hidden="true">
            {dashPrefix}
          </span>
        )}
        <span className={styles.nodeLabel}>{node.name}</span>
      </button>

      {hasChildren && (
        <ul className={styles.childList} role="group">
          {node.children.map((c) => (
            <TreeNode
              key={c.fullPath}
              node={c}
              onSelect={onSelect}
              selectedFullPath={selectedFullPath}
              depth={depth + 1}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

TagsTreeComponent.propTypes = {
  tree: PropTypes.array,
  onSelect: PropTypes.func,
  selectedFullPath: PropTypes.string,
};
